import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X, ChevronDown, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import FlagIcon from '../voice/FlagIcon';
import {
  SUBS_RANGES,
  VIDEOS_RANGES,
  VIEWS_RANGES,
  getCategoryName,
  getCountryCode,
  getLanguageName,
  parseSubsInput,
} from './utils';

/**
 * NicheFinderFilters — barra inline (Sprint 3 v6).
 *
 * Layout em uma linha (wrap em telas estreitas):
 *   [🔍 Buscar] [Idioma ▼] [Inscritos ▼] [Views ▼] [Categoria ▼]
 *   [Monetizados] [Ordenar ▼]
 *
 * O botao "Filtros" foi MOVIDO pra um toolbar a parte (rederizado em
 * NicheFinderPage, acima das tabs, alinhado a direita). Aqui ficaram so
 * os controles inline.
 *
 * Os dropdowns Inscritos/Views escrevem direto em subsMin/subsMax /
 * viewsMin/viewsMax — não há um campo "range enum" no shape do filtro.
 */
const NicheFinderFilters = ({
  filters,
  onFilterChange,
  onClear,
  hasActiveFilters,
  availableLanguages = [],
  availableCategories = [],
}) => {
  const { t } = useTranslation();

  // Debounce do search (200ms)
  const [localSearch, setLocalSearch] = useState(filters.search || '');
  useEffect(() => { setLocalSearch(filters.search || ''); }, [filters.search]);
  useEffect(() => {
    if (localSearch === filters.search) return;
    const id = setTimeout(() => onFilterChange('search', localSearch), 200);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localSearch]);

  // ===== Idioma (multi-check) =====
  const langOptions = useMemo(() => (
    (availableLanguages || []).map((iso) => ({
      value: iso,
      label: getLanguageName(iso, t),
      iconCode: getCountryCode(iso),
    }))
  ), [availableLanguages, t]);

  // ===== Categoria (multi-check) =====
  const catOptions = useMemo(() => (
    (availableCategories || []).map((c) => ({
      value: c,
      label: getCategoryName(c, t) || c,
    }))
  ), [availableCategories, t]);

  // ===== Sort — 'most_views' eh o default (Pacote 7.5); aparece primeiro ===== */
  const sortOptions = [
    { value: 'most_views',      label: t('viralNiches.sort_most_views') },
    { value: 'views_per_video', label: t('viralNiches.sort_views_per_video') },
    { value: 'fewest_videos',   label: t('viralNiches.sort_fewest_videos') },
    { value: 'most_videos',     label: t('viralNiches.sort_most_videos') },
    { value: 'highest_multi',   label: t('viralNiches.sort_highest_multi') },
    { value: 'most_subs',       label: t('viralNiches.sort_most_subs') },
    { value: 'most_recent',     label: t('viralNiches.sort_most_recent') },
  ];

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 8,
        padding: '10px 12px',
        background: 'var(--dp-bg-secondary)',
        border: '0.5px solid var(--dp-border-subtle)',
        borderRadius: 12,
        marginBottom: 12,
        fontFamily: 'var(--dp-font)',
      }}
    >
      {/* ===== Search com clear interno ===== */}
      <div style={{ flex: '1 1 240px', minWidth: 200, position: 'relative' }}>
        <Search
          size={14}
          style={{
            position: 'absolute',
            left: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--dp-text-tertiary)',
            pointerEvents: 'none',
          }}
        />
        <input
          type="text"
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          placeholder={t('viralNiches.search_placeholder')}
          className="dp-input"
          style={{
            paddingLeft: 30,
            paddingRight: hasActiveFilters ? 88 : (localSearch ? 30 : 10),
            height: 34,
          }}
        />
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={onClear}
            aria-label={t('viralNiches.filter_clear_all')}
            style={{
              position: 'absolute',
              right: 6,
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              height: 24,
              padding: '0 8px',
              borderRadius: 999,
              border: '0.5px solid var(--dp-border-medium)',
              background: 'transparent',
              color: 'var(--dp-text-secondary)',
              fontSize: 11,
              fontWeight: 500,
              fontFamily: 'var(--dp-font)',
              cursor: 'pointer',
              transition: 'color 0.15s, border-color 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'var(--dp-text-primary)';
              e.currentTarget.style.borderColor = 'var(--dp-border-strong)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'var(--dp-text-secondary)';
              e.currentTarget.style.borderColor = 'var(--dp-border-medium)';
            }}
          >
            <X size={11} />
            <span>{t('viralNiches.filter_clear_all')}</span>
          </button>
        ) : localSearch ? (
          <button
            type="button"
            onClick={() => setLocalSearch('')}
            aria-label="clear search"
            style={{
              position: 'absolute',
              right: 6,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 22, height: 22,
              borderRadius: 4,
              background: 'transparent',
              border: 'none',
              color: 'var(--dp-text-tertiary)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={12} />
          </button>
        ) : null}
      </div>

      {/* ===== Idioma (multi-check) ===== */}
      <MultiCheckDropdown
        label={t('viralNiches.filter_language')}
        options={langOptions}
        selected={filters.languages}
        onChange={(arr) => onFilterChange('languages', arr)}
        showFlags
      />

      {/* ===== Inscritos (range presets + custom) ===== */}
      <RangeDropdown
        label={t('viralNiches.filter_subscribers')}
        ranges={SUBS_RANGES}
        rangeLabelKey="viralNiches.subs_range_"
        min={filters.subsMin}
        max={filters.subsMax}
        onChange={(min, max) => {
          onFilterChange('subsMin', min);
          onFilterChange('subsMax', max);
        }}
      />

      {/* ===== Views (range presets + custom) ===== */}
      <RangeDropdown
        label={t('viralNiches.filter_views')}
        ranges={VIEWS_RANGES}
        rangeLabelKey="viralNiches.views_range_"
        min={filters.viewsMin}
        max={filters.viewsMax}
        onChange={(min, max) => {
          onFilterChange('viewsMin', min);
          onFilterChange('viewsMax', max);
        }}
      />

      {/* ===== Nº de vídeos (range presets + custom) — achar canais com
           poucos vídeos e muitas views (pedido de usuário 2026-07-11) ===== */}
      <RangeDropdown
        label={t('viralNiches.filter_videos')}
        ranges={VIDEOS_RANGES}
        rangeLabelKey="viralNiches.videos_range_"
        min={filters.videosMin}
        max={filters.videosMax}
        onChange={(min, max) => {
          onFilterChange('videosMin', min);
          onFilterChange('videosMax', max);
        }}
      />

      {/* ===== Categoria (multi-check) ===== */}
      <MultiCheckDropdown
        label={t('viralNiches.filter_category')}
        options={catOptions}
        selected={filters.categories}
        onChange={(arr) => onFilterChange('categories', arr)}
      />

      {/* ===== Toggles ===== */}
      {/* Pacote 7.6: filtro "Faceless apenas" removido — toda a base do Niche
          Finder ja e faceless (foco da plataforma). A metrica % FACELESS no
          card permanece. */}
      <ToggleButton
        active={filters.monetized === true}
        onClick={() => onFilterChange('monetized', filters.monetized === true ? null : true)}
        label={t('viralNiches.filter_monetized_only')}
      />

      {/* ===== Sort ===== */}
      <SingleSelectDropdown
        label={t('viralNiches.filter_sort')}
        options={sortOptions}
        selected={filters.sortBy}
        onChange={(v) => onFilterChange('sortBy', v)}
      />
    </div>
  );
};

// ============================================================
// Helpers
// ============================================================

function formatRangeBadge(min, max) {
  const isSet = (v) => v !== null && v !== undefined && !Number.isNaN(v);
  if (!isSet(min) && !isSet(max)) return null;
  const fmt = (n) => {
    if (!isSet(n)) return '∞';
    if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
    return String(n);
  };
  return `${fmt(min)}–${fmt(max)}`;
}

function findPresetIndex(ranges, min, max) {
  for (let i = 0; i < ranges.length; i += 1) {
    const r = ranges[i];
    if (r.value === 'all' || r.value === 'custom') continue;
    if (r.min === min && r.max === max) return i;
  }
  return -1;
}

// ============================================================
// Sub-componentes
// ============================================================

const ToggleButton = ({ active, onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      padding: '7px 12px',
      height: 34,
      background: active ? 'rgba(13,174,255,0.10)' : 'transparent',
      border: `0.5px solid ${active ? 'rgba(13,174,255,0.30)' : 'var(--dp-border-medium)'}`,
      borderRadius: 8,
      color: active ? 'var(--dp-blue-500)' : 'var(--dp-text-secondary)',
      fontSize: 12,
      fontWeight: 500,
      fontFamily: 'var(--dp-font)',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    }}
  >
    {label}
  </button>
);

const SingleSelectDropdown = ({ label, options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '7px 12px',
          height: 34,
          background: 'transparent',
          border: '0.5px solid var(--dp-border-medium)',
          borderRadius: 8,
          color: 'var(--dp-text-secondary)',
          fontSize: 12,
          fontWeight: 500,
          fontFamily: 'var(--dp-font)',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        <span>{label}</span>
        <ChevronDown size={12} />
      </button>
      {open && options && options.length > 0 && (
        <div
          role="listbox"
          className="dp-thin-scrollbar"
          style={{
            position: 'absolute',
            top: 38,
            right: 0,
            zIndex: 30,
            minWidth: 200,
            maxHeight: 300,
            overflowY: 'auto',
            background: 'var(--dp-bg-elevated)',
            border: '1px solid var(--dp-border-medium)',
            borderRadius: 10,
            padding: 4,
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          }}
        >
          {options.map((opt) => {
            const sel = selected === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={sel}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '7px 10px',
                  background: sel ? 'var(--dp-blue-subtle)' : 'transparent',
                  border: 'none',
                  borderRadius: 6,
                  color: sel ? 'var(--dp-blue-500)' : 'var(--dp-text-primary)',
                  fontSize: 13,
                  fontFamily: 'var(--dp-font)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                }}
              >
                <span>{opt.label}</span>
                {sel && <Check size={12} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const MultiCheckDropdown = ({ label, options, selected, onChange, showFlags = false }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const activeCount = Array.isArray(selected) ? selected.length : 0;
  const handleToggle = (value) => {
    const arr = Array.isArray(selected) ? [...selected] : [];
    const idx = arr.indexOf(value);
    if (idx >= 0) arr.splice(idx, 1);
    else arr.push(value);
    onChange(arr);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '7px 12px',
          height: 34,
          background: activeCount > 0 ? 'rgba(13,174,255,0.10)' : 'transparent',
          border: `0.5px solid ${activeCount > 0 ? 'rgba(13,174,255,0.30)' : 'var(--dp-border-medium)'}`,
          borderRadius: 8,
          color: activeCount > 0 ? 'var(--dp-blue-500)' : 'var(--dp-text-secondary)',
          fontSize: 12,
          fontWeight: 500,
          fontFamily: 'var(--dp-font)',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        <span>{label}</span>
        {activeCount > 0 && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              background: 'rgba(13,174,255,0.15)',
              color: 'var(--dp-blue-500)',
              padding: '0 6px',
              borderRadius: 999,
            }}
          >
            {activeCount}
          </span>
        )}
        <ChevronDown size={12} />
      </button>
      {open && options && options.length > 0 && (
        <div
          role="listbox"
          className="dp-thin-scrollbar"
          style={{
            position: 'absolute',
            top: 38,
            left: 0,
            zIndex: 30,
            minWidth: 220,
            maxHeight: 320,
            overflowY: 'auto',
            background: 'var(--dp-bg-elevated)',
            border: '1px solid var(--dp-border-medium)',
            borderRadius: 10,
            padding: 4,
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          }}
        >
          {options.map((opt) => {
            const sel = Array.isArray(selected) && selected.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={sel}
                onClick={() => handleToggle(opt.value)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '7px 10px',
                  background: sel ? 'var(--dp-blue-subtle)' : 'transparent',
                  border: 'none',
                  borderRadius: 6,
                  color: sel ? 'var(--dp-blue-500)' : 'var(--dp-text-primary)',
                  fontSize: 13,
                  fontFamily: 'var(--dp-font)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  {showFlags && opt.iconCode && (
                    <FlagIcon code={opt.iconCode} size="16px" rounded />
                  )}
                  <span>{opt.label}</span>
                </span>
                {sel && <Check size={12} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const RangeDropdown = ({ label, ranges, rangeLabelKey, min, max, onChange }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // customMode: user clicou em "Personalizado" — abre os inputs no
  // dropdown MESMO se min/max ainda estiverem null. Sem isso, o usuario
  // clicava em "Personalizado" e nada acontecia visualmente.
  const [customMode, setCustomMode] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const isActive = (min !== null && min !== undefined && !Number.isNaN(min))
                || (max !== null && max !== undefined && !Number.isNaN(max));
  const presetIdx = findPresetIndex(ranges, min, max);
  // showCustom: mostra inputs sempre que (a) usuario clicou Personalizado
  // OU (b) existem valores que nao casam com nenhum preset (custom de fato).
  const showCustom = customMode || (isActive && presetIdx < 0);
  const customLabel = showCustom && isActive ? formatRangeBadge(min, max) : null;

  // Quando um preset e' selecionado externamente, sair do customMode.
  useEffect(() => {
    if (presetIdx >= 0) setCustomMode(false);
  }, [presetIdx]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '7px 12px',
          height: 34,
          background: isActive ? 'rgba(13,174,255,0.10)' : 'transparent',
          border: `0.5px solid ${isActive ? 'rgba(13,174,255,0.30)' : 'var(--dp-border-medium)'}`,
          borderRadius: 8,
          color: isActive ? 'var(--dp-blue-500)' : 'var(--dp-text-secondary)',
          fontSize: 12,
          fontWeight: 500,
          fontFamily: 'var(--dp-font)',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        <span>{label}</span>
        {customLabel && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              background: 'rgba(13,174,255,0.15)',
              color: 'var(--dp-blue-500)',
              padding: '0 6px',
              borderRadius: 999,
            }}
          >
            {customLabel}
          </span>
        )}
        {!customLabel && presetIdx >= 0 && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              background: 'rgba(13,174,255,0.15)',
              color: 'var(--dp-blue-500)',
              padding: '0 6px',
              borderRadius: 999,
            }}
          >
            {t(`${rangeLabelKey}${ranges[presetIdx].value}`)}
          </span>
        )}
        <ChevronDown size={12} />
      </button>
      {open && (
        <div
          className="dp-thin-scrollbar"
          style={{
            position: 'absolute',
            top: 38,
            left: 0,
            zIndex: 30,
            minWidth: 220,
            background: 'var(--dp-bg-elevated)',
            border: '1px solid var(--dp-border-medium)',
            borderRadius: 10,
            padding: 4,
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          }}
        >
          {ranges.map((r) => {
            const isAll = r.value === 'all';
            const isCustom = r.value === 'custom';
            const sel = isAll
              ? (!isActive && !customMode)
              : isCustom
                ? showCustom
                : (r.min === min && r.max === max);
            return (
              <button
                key={r.value}
                type="button"
                role="option"
                aria-selected={sel}
                onClick={() => {
                  if (isAll) {
                    setCustomMode(false);
                    onChange(null, null);
                    setOpen(false);
                  } else if (isCustom) {
                    // Abre os inputs De/Ate no proprio dropdown
                    // (nao fecha — usuario edita inline).
                    setCustomMode(true);
                  } else {
                    setCustomMode(false);
                    onChange(r.min, r.max);
                    setOpen(false);
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '7px 10px',
                  background: sel ? 'var(--dp-blue-subtle)' : 'transparent',
                  border: 'none',
                  borderRadius: 6,
                  color: sel ? 'var(--dp-blue-500)' : 'var(--dp-text-primary)',
                  fontSize: 13,
                  fontFamily: 'var(--dp-font)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                }}
              >
                <span>{t(`${rangeLabelKey}${r.value}`)}</span>
                {sel && <Check size={12} />}
              </button>
            );
          })}
          {showCustom && (
            <CustomRangeInputs
              minLabel={t('viralNiches.range_from')}
              maxLabel={t('viralNiches.range_to')}
              minValue={min}
              maxValue={max}
              onMinChange={(v) => onChange(v, max)}
              onMaxChange={(v) => onChange(min, v)}
            />
          )}
        </div>
      )}
    </div>
  );
};

const CustomRangeInputs = ({
  minLabel, maxLabel, minValue, maxValue, onMinChange, onMaxChange,
}) => {
  const [minStr, setMinStr] = useState(
    minValue !== null && minValue !== undefined ? String(minValue) : '',
  );
  const [maxStr, setMaxStr] = useState(
    maxValue !== null && maxValue !== undefined ? String(maxValue) : '',
  );

  // Sync from props quando mudar externamente (ex: reset via "Limpar").
  // NAO sobrescreve se o que o usuario digitou ja representa o mesmo valor
  // (ex: usuario digita "1k", prop volta como 1000 — manter "1k" pra nao
  // perder o que ele esta digitando).
  useEffect(() => {
    const ext = minValue !== null && minValue !== undefined ? String(minValue) : '';
    setMinStr((cur) => (parseSubsInput(cur) === minValue ? cur : ext));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minValue]);
  useEffect(() => {
    const ext = maxValue !== null && maxValue !== undefined ? String(maxValue) : '';
    setMaxStr((cur) => (parseSubsInput(cur) === maxValue ? cur : ext));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxValue]);

  // Debounce 300ms — aplica filtro em tempo real conforme usuario digita,
  // sem disparar refilter a cada tecla.
  useEffect(() => {
    const parsed = parseSubsInput(minStr);
    if (parsed === minValue) return;
    const id = setTimeout(() => onMinChange(parsed), 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minStr]);
  useEffect(() => {
    const parsed = parseSubsInput(maxStr);
    if (parsed === maxValue) return;
    const id = setTimeout(() => onMaxChange(parsed), 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxStr]);

  // Validacao: min > max marca ambos os inputs com borda vermelha
  // (aplica mesmo assim — feedback visual sem bloquear o usuario).
  const parsedMin = parseSubsInput(minStr);
  const parsedMax = parseSubsInput(maxStr);
  const isInvalid = parsedMin !== null && parsedMax !== null && parsedMin > parsedMax;

  const inputStyle = (invalid) => ({
    width: 80,
    height: 28,
    padding: '4px 8px',
    background: 'var(--dp-bg-input)',
    border: `1px solid ${invalid ? 'var(--dp-red-500, #ef4444)' : 'var(--dp-border-subtle)'}`,
    borderRadius: 6,
    color: 'var(--dp-text-primary)',
    fontSize: 12,
    fontFamily: 'var(--dp-font)',
    outline: 'none',
  });

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '8px 10px',
        borderTop: '1px solid var(--dp-border-subtle)',
        marginTop: 4,
      }}
    >
      <input
        type="text"
        value={minStr}
        onChange={(e) => setMinStr(e.target.value)}
        placeholder={minLabel}
        aria-label={minLabel}
        style={inputStyle(isInvalid)}
      />
      <span style={{ color: 'var(--dp-text-tertiary)', fontSize: 12 }}>—</span>
      <input
        type="text"
        value={maxStr}
        onChange={(e) => setMaxStr(e.target.value)}
        placeholder={maxLabel}
        aria-label={maxLabel}
        style={inputStyle(isInvalid)}
      />
    </div>
  );
};

export default NicheFinderFilters;
